import { dayKey, dayLabel, formatElapsed, shiftDay } from "../timesheet";

import styles from "./DaySelector.module.css";

interface DaySelectorProps {
  /** The day shown, `YYYY-MM-DD`. */
  day: string;
  /** The page's one clock, in epoch milliseconds. */
  now: number;
  /** The person chose a day. */
  onDay: (day: string) => void;
  /** The day's total in milliseconds. It is drawn only when the page gives it. */
  total?: number | undefined;
  /** How many rows on other days still hold unsent time. */
  unpublishedElsewhere?: number | undefined;
}

/**
 * The day selector: a step back, the date this page shows, a step forward, the
 * day's name, its total, and a Today button. The button is drawn only when the
 * page shows another day. Every date step comes from `shiftDay`.
 */
export function DaySelector({
  day,
  now,
  onDay,
  total,
  unpublishedElsewhere = 0,
}: DaySelectorProps) {
  const today = dayKey(now);

  return (
    <div className={styles.selector}>
      <button
        type="button"
        className={styles.step}
        aria-label="The day before"
        onClick={() => onDay(shiftDay(day, -1))}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.stepIcon}>
          <path d="M15 18 9 12l6-6" />
        </svg>
      </button>

      <input
        type="date"
        className={styles.date}
        aria-label="The day this page shows"
        value={day}
        // An empty date input names no day, so the page keeps the day it holds.
        onChange={(event) => {
          if (event.target.value !== "") onDay(event.target.value);
        }}
      />

      <button
        type="button"
        className={styles.step}
        aria-label="The day after"
        onClick={() => onDay(shiftDay(day, 1))}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.stepIcon}>
          <path d="m9 6 6 6-6 6" />
        </svg>
      </button>

      <span className={styles.name}>{dayLabel(day, now)}</span>

      {total !== undefined && <span className={styles.total}>{formatElapsed(total)}</span>}

      {day !== today && (
        <button type="button" className={styles.today} onClick={() => onDay(today)}>
          Today
        </button>
      )}

      {unpublishedElsewhere > 0 && (
        <span className={styles.elsewhere}>
          {unpublishedElsewhere === 1
            ? "1 row on another day still holds unsent time."
            : `${unpublishedElsewhere} rows on other days still hold unsent time.`}
        </span>
      )}
    </div>
  );
}
